import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { FormlyErrorComponent } from '../formly-error.component';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { ClearableFieldType } from './clearable-field.type';

@Component({
  selector: 'formly-field-date-week',
  template: `
    <mat-form-field class="w-full" [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
      <mat-label>
        <app-field-label-row
          [label]="props.label + (field.props.required ? ' *' : '')"
          [required]="field.props.required"
          [info]="props.info"></app-field-label-row>
      </mat-label>
      <!-- The visible input is a display mirror of the hidden Material control, so it carries
           neither matInput nor MatDatepickerInput. Keep its explicit click and ALT+ARROW_DOWN
           routes alongside the toggle restored by the focused state. -->
      <!-- [disabled] as well as readonly: disabling the field disables the hidden control, but
           the mirror kept its own click/ALT+ARROW_DOWN routes, so a disabled week field still
           opened its picker and stayed focusable (caught by the disabled invariant on the new
           Velden/Datum en tijd/Weeknummer story). -->
      <input
        #weekInput
        [value]="displayValue"
        [attr.aria-label]="props.label"
        [disabled]="isDisabled()"
        readonly
        (click)="picker.open()"
        (keydown.alt.arrowdown)="picker.open()" />
      <input
        class="hidden"
        matInput
        [formControl]="formControl"
        [formlyAttributes]="field"
        [matDatepicker]="picker"
        [value]="displayValue"
        (dateChange)="onDateChange($event)"
        readonly
        (click)="picker.open()" />
      <span class="dd-clear-suffix dd-addon-suffix" matSuffix>
        <mat-datepicker-toggle class="dd-addon-suffix__passive" [for]="picker"></mat-datepicker-toggle>
        <app-clear-field-button
          [field]="field"
          [hasValue]="hasClearableValue()"
          [disabled]="isDisabled()"
          [ariaLabel]="clearAriaLabel()"
          [focusTarget]="weekInput"
          (clearSelection)="clear()" />
      </span>
      <mat-datepicker #picker></mat-datepicker>
      @if (props.description) {
        <mat-hint>{{ props.description }}</mat-hint>
      }
    </mat-form-field>
    <app-formly-error [field]="field"></app-formly-error>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    FormlyErrorComponent,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
})
export class FormlyFieldDateWeekComponent extends ClearableFieldType<FieldTypeConfig> implements OnInit {
  displayValue: string = '';

  constructor() {
    super();
  }

  ngOnInit() {
    if (this.formControl.value) {
      this.updateDisplayFromWeekString(this.formControl.value);
    }
  }

  onDateChange(event: any) {
    if (event.value) {
      this.updateWeekValue(event.value);
    }
  }

  // Deviates from the base: the "Week N, YYYY" text is rendered from a derived field, not from the
  // control value, so clearing has to reset that projection as well.
  override clear(): void {
    super.clear();
    this.displayValue = '';
  }

  private updateWeekValue(date) {
    const selectedDate = new Date(date['ts']);
    const weekNum = this.getWeekNumber(selectedDate);
    const year = selectedDate.getFullYear();
    const weekString = `${year}-W${weekNum.toString().padStart(2, '0')}`;

    if (weekString !== this.formControl.value) {
      this.formControl.setValue(weekString);
    }
    this.updateDisplayFromWeekString(weekString);
  }

  private getWeekNumber(date: Date): number {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    return weekNo;
  }

  private updateDisplayFromWeekString(weekString: string) {
    const [year, week] = weekString.split('-W');
    this.displayValue = `Week ${parseInt(week)}, ${year}`;
  }
}
